import React from "react";
import { FaEdit, FaEye, FaFilePdf, FaImage, FaTrash } from "react-icons/fa";
import Button from "../../../../components/Button/Button";

const toAbsoluteUrl = (u) => {
  if (!u) return "";
  const s = String(u).trim();

  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  ) {
    return s;
  }

  if (!s.startsWith("/uploads")) return s;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const isQuillEmpty = (html = "") => {
  const cleaned = String(html)
    .replace(/<p><br><\/p>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();

  return !cleaned;
};

export default function PassageCeintureDocumentCard({
  document,
  canManage,
  onOpen,
  onEdit,
  onDelete,
}) {
  const imageUrl = toAbsoluteUrl(document?.image_url);
  const pdfUrl = toAbsoluteUrl(document?.pdf_url);
  const hasDescription = !isQuillEmpty(document?.description);

  return (
    <article className="group rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm hover:shadow-lg transition-all duration-300">
      <div className="relative h-44 bg-slate-100 dark:bg-slate-900 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={document?.titre || "Document"}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-slate-400">
            <FaImage className="text-4xl" />
          </div>
        )}

        <div className="absolute left-3 top-3">
          <span className="inline-flex rounded-full bg-slate-900/75 text-white text-xs font-semibold px-3 py-1">
            {document?.ceinture || "Sans ceinture"}
          </span>
        </div>

        {!document?.is_published && (
          <div className="absolute right-3 top-3">
            <span className="inline-flex rounded-full bg-amber-500 text-white text-xs font-semibold px-3 py-1">
              Brouillon
            </span>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 line-clamp-2">
            {document?.titre || "Sans titre"}
          </h3>

          {hasDescription ? (
            <div
              className="mt-2 text-sm text-slate-600 dark:text-slate-300 min-h-[84px] max-h-[84px] overflow-hidden
              [&_p]:mb-1 [&_p:last-child]:mb-0
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-1
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-1
              [&_strong]:font-semibold [&_em]:italic
              [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: document.description }}
            />
          ) : (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 min-h-[84px]">
              Aucune description disponible.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {imageUrl && (
            <a
              href={imageUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
            >
              <FaImage />
              Image
            </a>
          )}

          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition"
            >
              <FaFilePdf />
              PDF
            </a>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
            <Button
              text="Modifier"
              icon={FaEdit}
              onClick={onEdit}
              className="rounded-xl"
            />
            <Button
              text="Supprimer"
              icon={FaTrash}
              onClick={onDelete}
              className="rounded-xl !bg-red-600 hover:!bg-red-700"
            />
          </div>
        )}
      </div>
    </article>
  );
}
